<template>
  <div
    ref="containerDiv"
    :class="{ 'cursor-pointer': !disable && !disabledStatusCircle }"
    @click.stop="show"
    @hover.stop="show"
    @mouseover.stop="show"
    @touchstart.passive.stop="show"
    @mouseleave="resetTimer(1500)"
  >
    <ProgressSpinner v-if="loading" class="square24px" />
    <StatusCircleComponent
      v-else
      class="w-100"
      :status="modelValue"
      :class="{ 'cursor-pointer': !disable && !disabledStatusCircle }"
    />
    <Popover v-if="!disable && !disabledStatusCircle" ref="op" class="StatusCircleCustomScope mt-0">
      <div class="taskStatusCircles" @mouseenter="stopTimer">
        <slot :reset-timer="resetTimer" :stop-timer="stopTimer">
          <div class="flex flex-column gap-1" @mouseleave="resetTimer(700)">
            <div class="p-1" @click.stop="updateActionStatus(1)">
              <StatusCircleComponent :status="1" style="height: 1.5rem" />
              {{ t("views.pdca.plan") }}
            </div>
            <div class="p-1" @click.stop="updateActionStatus(2)">
              <StatusCircleComponent :status="2" style="height: 1.5rem" />
              {{ t("views.pdca.do") }}
            </div>
            <div class="p-1" @click.stop="updateActionStatus(3)">
              <StatusCircleComponent :status="3" style="height: 1.5rem" />
              {{ t("views.pdca.check") }}
            </div>
            <div class="p-1" @click.stop="updateActionStatus(4)">
              <StatusCircleComponent :status="4" style="height: 1.5rem" />
              {{ t("views.pdca.act") }}
            </div>
            <template v-if="showActAndArchive">
              <hr class="w-100 my-0" />
              <div
                :class="{ disabled: disableActAndArchiveReason != null }"
                @click.stop="updateActionStatus(4, true)"
              >
                <InfoTooltip
                  :disabled="disableActAndArchiveReason == null"
                  :text="disableActAndArchiveReason"
                >
                  <template #activator>
                    <div
                      class="flex align-items-center gap-2 p-1"
                      :style="`color: var(${disableActAndArchiveReason != null ? '--p-form-field-disabled-color' : '--p-info-color'})`"
                    >
                      <StatusCircleComponent
                        style="height: 1.5rem"
                        :status="4"
                        :filling-color="`var(${disableActAndArchiveReason != null ? '--p-form-field-disabled-color' : '--p-info-color'})`"
                      />
                      {{
                        showOnlyArchiveText ? t("views.pdca.archive") : t("views.pdca.actArchive")
                      }}
                    </div>
                  </template>
                </InfoTooltip>
              </div>
            </template>
          </div>
        </slot>
      </div>
    </Popover>
  </div>
</template>

<script setup lang="ts">
import { useTranslation } from "@/language/translation";

import ProgressSpinner from "primevue/progressspinner";
import { inject, ref } from "vue";
import StatusCircleComponent from "../task/StatusCircleComponent.vue";
import { DisabledStateInjectionKey } from "./IssueTypes";
import Popover from "primevue/popover";
import InfoTooltip from "@/components/shared/InfoTooltip.vue";

const props = defineProps({
  modelValue: {
    type: Number,
    required: false,
    default: 1,
  },
  loading: {
    type: Boolean,
    required: false,
    default: false,
  },
  disable: {
    type: Boolean,
    default: false,
  },
  showActAndArchive: {
    type: Boolean,
    default: false,
  },
  disableActAndArchiveReason: {
    type: String,
    default: undefined,
  },
  showOnlyArchiveText: {
    type: Boolean,
    default: false,
  },
});
const emit = defineEmits({
  "update:modelValue": (val: number) => val,
  archiveEvent: (val: boolean) => val,
});
const disabledStatusCircle = inject(DisabledStateInjectionKey, false);

const op = ref<InstanceType<typeof Popover>>();
const timer = ref<number>();

function show(event: Event) {
  clearTimeout(timer.value);
  op.value?.show(event);
}

function hideOverlay() {
  op.value?.hide();
}

function resetTimer(interval: number) {
  timer.value = setTimeout(() => {
    hideOverlay();
  }, interval);
}

function stopTimer() {
  clearTimeout(timer.value);
}

async function updateActionStatus(status: number, archive = false) {
  if (archive && props.disableActAndArchiveReason) {
    return;
  }
  hideOverlay();
  if (archive) {
    emit("archiveEvent", true);
  } else {
    emit("update:modelValue", status);
  }
}

const { t } = useTranslation();
</script>

<style lang="scss">
.StatusCircleCustomScope {
  .p-popover-content {
    padding: 0.5rem !important;
  }
}
</style>

<style lang="scss" scoped>
.square24px {
  width: 100%;
  height: 100%;
}
.taskStatusCircles > div {
  padding-bottom: 0.25rem;
  > div {
    display: flex;
    gap: 0.25rem;
    align-items: center;

    &:hover {
      background: rgb(226 226 226);
      border-radius: 5px;
    }

    &:not(.disabled) {
      cursor: pointer;
    }

    &.disabled {
      background: var(--p-disabled-color) !important;
      border-radius: 5px;
      cursor: unset;
    }
  }
}
</style>
